import { useState, useEffect, useCallback, useRef } from "react";
import { Pagination, Segmented, Skeleton, Empty, Tag, Tooltip, Button, message, Popconfirm, Select } from "antd";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, MessageCircle, MessagesSquare, PhoneOff, UserPlus, Users, Building2, Stethoscope, Check, CheckCheck } from "lucide-react";
import Layout from "../../Layout";
import { getNotifications, markNotificationRead, markAllNotificationsRead, NotificationItem } from "../../api/Notification";

/**
 * Trang danh sách thông báo (/notifications).
 * - Lọc: Tất cả / Chưa đọc / Đã đọc (map query is_read của API).
 * - Phân trang dạng số (antd Pagination), đổi được cỡ trang 15/30/50.
 * - Click thông báo: đánh dấu đã đọc + điều hướng tới trang liên quan theo loại
 *   (đồng bộ với dropdown chuông ở DashboardHeader).
 */

// Icon + màu nhận diện theo loại thông báo (đủ 7 loại theo tài liệu API)
const TYPE_META: Record<string, { Icon: any; cls: string }> = {
    new_message: { Icon: MessageCircle, cls: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300" },
    staff_message_support: { Icon: MessagesSquare, cls: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-300" },
    noti_staff: { Icon: Users, cls: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300" },
    noti_telesale: { Icon: UserPlus, cls: "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300" },
    noti_branch_staff: { Icon: Building2, cls: "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300" },
    noti_action_staff: { Icon: Stethoscope, cls: "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300" },
    call_failed: { Icon: PhoneOff, cls: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300" },
};
const DEFAULT_META = { Icon: Bell, cls: "bg-gray-100 text-gray-500 dark:bg-[#303030] dark:text-gray-300" };

// Thứ tự loại thông báo trong ô lọc
const NOTIF_TYPES = [
    "new_message",
    "staff_message_support",
    "noti_staff",
    "noti_telesale",
    "noti_branch_staff",
    "noti_action_staff",
    "call_failed",
];

const PAGE_SIZE_DEFAULT = 15;

type FilterKey = "all" | "unread" | "read";

export default function NotificationsPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [items, setItems] = useState<NotificationItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [filter, setFilter] = useState<FilterKey>("all");
    // Lọc theo loại thông báo (query "type" của API); null = tất cả loại
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const listTopRef = useRef<HTMLDivElement>(null);

    // API trả {items} hoặc paginator {data}
    const extractItems = (data: any): NotificationItem[] => {
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.data)) return data.data;
        return [];
    };

    // silent = true: nạp lại nền (realtime) không hiện skeleton để danh sách không bị nháy
    const fetchList = useCallback(async (p: number, size: number, f: FilterKey, ty: string | null, silent = false) => {
        if (!silent) setLoading(true);
        const res = await getNotifications({
            page: p,
            limit: size,
            is_read: f === "all" ? undefined : f === "read",
            type: ty || undefined,
        });
        if (res.status && res.data) {
            setItems(extractItems(res.data));
            setTotal(res.data.total ?? 0);
        } else if (!silent) {
            setItems([]);
            setTotal(0);
        }
        if (!silent) setLoading(false);
    }, []);

    useEffect(() => {
        fetchList(page, pageSize, filter, typeFilter);
    }, [fetchList, page, pageSize, filter, typeFilter]);

    // Có thông báo mới (tín hiệu realtime do DashboardHeader phát) -> nạp lại danh sách nền
    useEffect(() => {
        const onArrived = () => fetchList(page, pageSize, filter, typeFilter, true);
        window.addEventListener("notification-arrived", onArrived);
        return () => window.removeEventListener("notification-arrived", onArrived);
    }, [fetchList, page, pageSize, filter, typeFilter]);

    // Đổi bộ lọc -> quay về trang 1
    const handleFilterChange = (val: FilterKey) => {
        setFilter(val);
        setPage(1);
    };

    const handlePageChange = (p: number, size: number) => {
        setPage(size !== pageSize ? 1 : p);
        setPageSize(size);
        // Cuộn lên đầu danh sách cho dễ đọc từ trên xuống
        listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    // Đánh dấu TẤT CẢ thông báo là đã đọc, rồi nạp lại danh sách + báo header cập nhật badge
    const [markingAll, setMarkingAll] = useState(false);
    const handleMarkAllRead = async () => {
        if (markingAll) return;
        setMarkingAll(true);
        const res = await markAllNotificationsRead();
        setMarkingAll(false);
        if (res.status) {
            message.success(t("notif.mark_all_success") || res.msg || "Đã đánh dấu đọc tất cả thành công");
            // Cập nhật ngay danh sách đang hiển thị + badge chuông ở header
            setItems((prev) => prev.map((it) => ({ ...it, is_read: true })));
            window.dispatchEvent(new Event("notifications-read-all"));
            // Nạp lại theo bộ lọc hiện tại (vd đang xem "Chưa đọc" thì danh sách sẽ rỗng)
            fetchList(1, pageSize, filter, typeFilter);
            setPage(1);
        } else {
            message.error(res.msg || t("notif.mark_all_failed") || "Đánh dấu đọc tất cả thất bại");
        }
    };

    // Đánh dấu đã đọc (optimistic, revert khi lỗi)
    const markRead = async (n: NotificationItem) => {
        if (n.is_read) return;
        setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: true } : it)));
        const res = await markNotificationRead(n.id);
        if (!res.status) {
            setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, is_read: false } : it)));
        } else {
            // Báo header cập nhật lại số chưa đọc trên chuông
            window.dispatchEvent(new Event("notification-read"));
        }
    };

    // Điều hướng tới trang liên quan theo loại (đồng bộ logic với DashboardHeader)
    const openRelated = (n: NotificationItem) => {
        markRead(n);
        if (n.type === "new_message" && n.channel_connection_id && n.channel_customer_id) {
            navigate(`/chat/${n.channel_connection_id}?customer_id=${n.channel_customer_id}`);
        } else if (n.type === "noti_telesale" && n.related_id) {
            navigate(`/appointment/detail/${n.related_id}`);
        } else if (n.type === "noti_branch_staff" && n.related_id) {
            navigate(`/consultation/detail/${n.related_id}`);
        } else if (n.type === "noti_action_staff" && n.related_id) {
            navigate(`/execution-schedule/detail/${n.related_id}`);
        } else if (n.type === "call_failed" && n.related_id) {
            navigate(`/customer/${n.related_id}`);
        }
    };

    const typeLabel = (type: string) => {
        const key = `notif.type_${type}`;
        const label = t(key);
        return label === key ? t("notif.type_other") : label;
    };

    const formatTime = (iso: string) => {
        try {
            return new Date(iso).toLocaleString(i18n.language === "vi" ? "vi-VN" : "en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>{t("header.notifications")} | Khothi</title>
            </Helmet>

            <div className="p-4 md:p-6 max-w-[900px] mx-auto" ref={listTopRef}>
                {/* Tiêu đề + bộ lọc */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <h1 className="text-xl font-semibold text-gray-800 dark:text-white m-0">
                        {t("header.notifications")}
                        <span className="ml-2 text-sm font-normal text-gray-400">({total})</span>
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Lọc theo loại thông báo — kèm icon màu trùng với icon trên từng dòng cho dễ nhận diện */}
                        <Select
                            allowClear
                            placeholder={t("notif.filter_type")}
                            className="min-w-[210px]"
                            value={typeFilter ?? undefined}
                            onChange={(val) => {
                                setTypeFilter(val ?? null);
                                setPage(1);
                            }}
                            options={NOTIF_TYPES.map((ty) => {
                                const meta = TYPE_META[ty] || DEFAULT_META;
                                const { Icon } = meta;
                                return {
                                    value: ty,
                                    label: (
                                        <span className="flex items-center gap-2">
                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center ${meta.cls}`}>
                                                <Icon size={12} />
                                            </span>
                                            {typeLabel(ty)}
                                        </span>
                                    ),
                                };
                            })}
                        />
                        <Segmented
                            value={filter}
                            onChange={(val) => handleFilterChange(val as FilterKey)}
                            options={[
                                { label: t("notif.filter_all"), value: "all" },
                                { label: t("notif.filter_unread"), value: "unread" },
                                { label: t("notif.filter_read"), value: "read" },
                            ]}
                        />
                        {/* Xác nhận trước khi đánh dấu tất cả để tránh bấm nhầm */}
                        <Popconfirm
                            title={t("notif.mark_all_confirm_title")}
                            description={t("notif.mark_all_confirm_desc")}
                            okText={t("notif.confirm_ok")}
                            cancelText={t("notif.confirm_cancel")}
                            onConfirm={handleMarkAllRead}
                            placement="bottomRight"
                        >
                            <Button
                                loading={markingAll}
                                icon={<CheckCheck size={14} className="inline-block -mt-0.5 mr-1" />}
                            >
                                {t("notif.mark_all_read")}
                            </Button>
                        </Popconfirm>
                    </div>
                </div>

                {/* Danh sách */}
                <div className="rounded-lg border border-gray-200 dark:border-[#303030] bg-white dark:bg-[#1f1f1f] overflow-hidden">
                    {loading ? (
                        <ul className="divide-y divide-gray-100 dark:divide-[#303030]">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <li key={`sk-${i}`} className="px-4 py-4">
                                    <Skeleton active avatar={{ shape: "circle", size: 40 }} title={{ width: "45%" }} paragraph={{ rows: 2, width: ["100%", "30%"] }} />
                                </li>
                            ))}
                        </ul>
                    ) : items.length === 0 ? (
                        <div className="py-16">
                            <Empty description={t("notif.empty")} />
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-[#303030]">
                            {items.map((n) => {
                                const meta = TYPE_META[n.type] || DEFAULT_META;
                                const { Icon } = meta;
                                return (
                                    <li
                                        key={n.id}
                                        onClick={() => openRelated(n)}
                                        className={`px-4 py-4 flex items-start gap-3 cursor-pointer transition hover:bg-gray-50 dark:hover:bg-[#262626] ${n.is_read ? "" : "bg-[#3e91ab]/5"}`}
                                    >
                                        {/* Icon loại thông báo */}
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${meta.cls}`}>
                                            <Icon size={18} />
                                        </div>

                                        {/* Nội dung */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#3e91ab] flex-shrink-0" />}
                                                <span className={`text-sm text-gray-800 dark:text-white ${n.is_read ? "font-normal" : "font-semibold"}`}>
                                                    {n.title}
                                                </span>
                                                <Tag className="m-0 text-[11px] leading-5" color={n.is_read ? "default" : "cyan"}>
                                                    {typeLabel(n.type)}
                                                </Tag>
                                            </div>
                                            {/* Hiển thị đầy đủ nội dung để xem chi tiết, giữ xuống dòng gốc */}
                                            <div className="text-[13px] text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line break-words">
                                                {n.content}
                                            </div>
                                            <div className="text-[11px] text-gray-400 mt-1.5">{formatTime(n.created_at)}</div>
                                        </div>

                                        {/* Đánh dấu đã đọc nhanh (không điều hướng) */}
                                        {!n.is_read && (
                                            <Tooltip title={t("notif.mark_read")}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); markRead(n); }}
                                                    className="p-1.5 rounded-md text-gray-400 hover:text-[#3e91ab] hover:bg-gray-100 dark:hover:bg-[#303030] transition flex-shrink-0"
                                                >
                                                    <Check size={16} />
                                                </button>
                                            </Tooltip>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Phân trang dạng số */}
                {total > 0 && (
                    <div className="flex justify-end mt-4">
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={total}
                            onChange={handlePageChange}
                            showSizeChanger
                            pageSizeOptions={[15, 30, 50]}
                            showTotal={(tt, range) => t("notif.total_range", { from: range[0], to: range[1], total: tt })}
                        />
                    </div>
                )}
            </div>
        </Layout>
    );
}
